<form [formGroup]="form" (ngSubmit)="onSubmit()" class="space-y-4">

	<!-- Type de document -->
	<div>
		<label for="documentType" class="block text-sm font-medium text-gray-700 mb-2">
			Type de document <span class="text-red-500">*</span>
		</label>
		<select id="documentType" formControlName="documentType"
			class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
			[class.border-red-500]="form.get('documentType')?.invalid && form.get('documentType')?.touched">
			<option value="">Sélectionner un type</option>
			<option *ngFor="let type of documentTypes" [value]="type.value || type._id">
				{{ type.label }}
			</option>
		</select>
		<p *ngIf="form.get('documentType')?.invalid && form.get('documentType')?.touched" class="mt-1 text-sm text-red-600">
			{{ getErrorMessage('documentType') }}
		</p>
	</div>

	<!-- Professionnel -->
	<div>
		<label for="professional" class="block text-sm font-medium text-gray-700 mb-2">
			Professionnel
		</label>
		<input type="text" id="professional" formControlName="professional"
			class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
			[class.border-red-500]="form.get('professional')?.invalid && form.get('professional')?.touched"
			(blur)="onProfessionalChange($event)" placeholder="Nom du professionnel de santé">
		<p *ngIf="form.get('professional')?.invalid && form.get('professional')?.touched" class="mt-1 text-sm text-red-600">
			{{ getErrorMessage('professional') }}
		</p>
	</div>

	<!-- Fichier -->
	<div>
		<label for="file" class="block text-sm font-medium text-gray-700 mb-2">
			Fichier <span class="text-red-500">*</span>
		</label>
		<div class="relative">
			<input type="file" id="file" (change)="onFileSelected($event)" class="hidden"
				accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
			<label for="file"
				class="flex items-center justify-center w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-blue-400 transition-colors"
				[class.border-red-500]="form.get('file')?.invalid && form.get('file')?.touched">
				<lucide-icon [img]="Upload" class="w-5 h-5 text-gray-400 mr-2"></lucide-icon>
				<span class="text-sm text-gray-600">
					{{ selectedFile ? selectedFile.name : 'Cliquez pour sélectionner un fichier' }}
				</span>
			</label>
		</div>
		<p *ngIf="selectedFile" class="mt-1 text-xs text-gray-500">
			Taille: {{ (selectedFile.size / 1024 / 1024).toFixed(2) }} MB
		</p>
		<p *ngIf="!selectedFile && form.get('file')?.touched" class="mt-1 text-sm text-red-600">
			Veuillez sélectionner un fichier
		</p>
	</div>

	<!-- Sélection du patient -->
	<div>
		<label class="block text-sm font-medium text-gray-700 mb-3">
			Pour quel patient ? <span class="text-red-500">*</span>
		</label>
		<div class="space-y-3">
			<!-- Patient principal -->
			<div *ngIf="currentUser" class="flex items-center">
				<input type="radio" id="patient-main" name="patient" [value]="currentUser.passportNumber"
					(change)="selectPatient(currentUser.passportNumber)"
					[checked]="selectedPatientId === currentUser.passportNumber" class="radio radio-sm">
				<label for="patient-main" class="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
					<div class="flex items-center">
						<span class="font-semibold">{{ currentUser.firstName }} {{ currentUser.lastName }}</span>
						<span class="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Principal</span>
					</div>
				</label>
			</div>

			<!-- Sous-comptes -->
			<div *ngFor="let account of subAccounts; let i = index" class="flex items-center">
				<input type="radio" [id]="'patient-' + i" name="patient" [value]="account.passportNumber"
					(change)="selectPatient(account.passportNumber)" [checked]="selectedPatientId === account.passportNumber"
					class="radio radio-sm">
				<label [for]="'patient-' + i" class="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
					<div class="flex items-center">
						<span class="font-semibold">{{ account.firstName }} {{ account.lastName }}</span>
						<span *ngIf="account.accountType === 'UNDERCOVER'"
							class="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">Sous-couvert</span>
					</div>
				</label>
			</div>

			<!-- Message si aucun patient -->
			<div *ngIf="!currentUser && subAccounts.length === 0" class="text-sm text-gray-500 italic">
				Aucun patient disponible
			</div>
		</div>
		<p *ngIf="!selectedPatientId && form.get('patientId')?.touched" class="mt-1 text-sm text-red-600">
			Veuillez sélectionner un patient
		</p>
	</div>

	<!-- Boutons d'action -->
	<div class="flex justify-end space-x-3 pt-6 border-t border-gray-200">
		<button type="button" (click)="closeModal()"
			class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm">
			Annuler
		</button>
		<button type="submit" [disabled]="loading || !isFormValid()"
			class="px-4 py-2 bg-primary text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm">
			<span *ngIf="loading" class="inline-flex items-center">
				<svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
					<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
					<path class="opacity-75" fill="currentColor"
						d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
					</path>
				</svg>
				Ajout...
			</span>
			<span *ngIf="!loading">Ajouter le document</span>
		</button>
	</div>
</form>